/* eslint-disable max-lines */
import { observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { UserResponseArgs } from '../user/user2';
import User, { convertUserResponseToArgs } from '../user/user2';
import type { TravelerInformationResponseArgs } from '../traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from '../traveler_information';
import type { InAdvanceApprovalResponseArgs } from '../in_advance_approval';
import { convertInAdvanceApprovalResponseToArgs, InAdvanceApproval } from '../in_advance_approval';
import type { ArrangementRequestResponseArgs } from '../arrangement_requiest';
import ArrangementRequest, { convertArrangementRequestResponseToArgs } from '../arrangement_requiest';
import type { OrderResponseArgs } from '../order2';
import Order, { convertOrderResponseToArgs } from '../order2';
import type OrderItem from '../order_item2';
import type { NonOrderItemResponseArgs } from '../non_order_item2';
import NonOrderItem, { convertNonOrderItemResponseToArgs } from '../non_order_item2';
import type { TripRuleErrorArgs } from './trip_rule_error';
import TripRuleError from './trip_rule_error';
import type { TripApproveItemResponseArgs } from '../trip_approve_item/trip_approve_item2';
import TripApproveItem, { convertTripApproveItemResponseToArgs } from '../trip_approve_item/trip_approve_item2';
import type { DepartmentResponseArgs } from '../department/department2';
import Department, { convertDepartmentResponseToArgs } from '../department/department2';
import type { SearchQueryResponseArgs } from '../search_query2';
import SearchQuery, { convertSearchQueryResponseToArgs } from '../search_query2';
import type TransportElement from '../transport_element2';
import type { Element as OrderItemElement } from '../order_item2';
import type { ArrangerMemoResponseArgs } from '../arranger_memo';
import ArrangerMemo, { convertArrangerMemoResponseToArgs } from '../arranger_memo';
import type { ProjectResponseArgs } from '../project/project2';
import Project, { convertProjectResponseToArgs } from '../project/project2';
import type { TripReportResponseArgs } from '../trip_report/trip_report/trip_report2';
import TripReport, { convertTripReportResponseToArgs } from '../trip_report/trip_report/trip_report2';
import type { TravelerNameChangeResponseArgs } from './traveler_name_change';
import TravelerNameChange, { convertTravelerNameChangeResponseToArgs } from './traveler_name_change';
import type { ForbiddenRepositoryArgs, OrganizationResponseArgs } from '../organization/organization3';
import Organization, { convertOrganizationResponseToArgs } from '../organization/organization3';

export type TripResponseArgs = {
  id: number;
  user: UserResponseArgs;
  organization?: OrganizationResponseArgs;
  user_id?: number;
  organization_id?: number;
  service_id?: number;
  search_query_id?: number;
  search_query?: SearchQueryResponseArgs;
  start_time?: string;
  end_time?: string;
  cache_start_time?: string;
  cache_end_time?: string;
  peoplenum?: number;
  final_destination?: string;
  purpose?: string;
  internal_number?: string;
  bakuraku_workflow_request_id?: string | null;
  bakuraku_workflow_request_title?: string | null;
  bakuraku_workflow_request_num?: string | null;
  invoice_trip_type?: string;
  flight_approve_expired_at?: string;
  current_order?: OrderResponseArgs;
  first_order?: OrderResponseArgs;
  applied_order?: OrderResponseArgs;
  approved_order?: OrderResponseArgs;
  traveler_informations?: TravelerInformationResponseArgs[];
  current_traveler_informations?: TravelerInformationResponseArgs[];
  cache_rule_errors?: TripRuleErrorArgs[];
  in_advance_approval?: InAdvanceApprovalResponseArgs[]; // serverから単数形で返ってくるのでここで吸収する
  current_approval_stage?: number;
  arrangement_request?: ArrangementRequestResponseArgs;
  non_order_items: NonOrderItemResponseArgs[];
  received_at?: string;
  created_at: string;
  status?: number;
  approve_stage_count?: number;
  progress?: string;
  charging_departments?: DepartmentResponseArgs[];
  projects?: ProjectResponseArgs[];
  orders?: OrderResponseArgs[];
  applicant?: UserResponseArgs;
  workflow_id?: number;
  billing?: boolean; // true: 請求書払い, false/undefined: カード払い
  forbidden_repositories?: ForbiddenRepositoryArgs[]; // 右ナビのSMAS利用の表示に使う
  itinerary_id?: string;
  trip_approve_item?: TripApproveItemResponseArgs;
  last_action_at?: string;
  updated_at?: string;
  exists_unopend_user_message?: boolean;
  arranger_memo?: ArrangerMemoResponseArgs;
  foreign_air?: boolean;
  ticketing_requested_at?: string;
  freee_approval_request_id: number | null;
  invoice_id?: number;
  departure_location?: string; // モデル上は存在するが値が入ることがない
  destination?: string; // モデル上は存在するが値が入ることがない
  downloaded_count?: number;
  downloaded_at?: string;
  draft?: boolean;
  has_unread_reply_required_messages?: boolean;
  exic_uri?: string;
  business_ekinet_uri?: string;
  ticketing_requestable?: boolean;
  seat_reservation_enable?: boolean;
  cancel_requestable?: boolean;
  bulk_cancelable?: boolean;
  bulk_cancel_error_message?: string | null;
  bulk_cancel_deadline_text?: string | null;
  premium_support_active?: boolean;
  report_available?: boolean;
  trip_report?: TripReportResponseArgs;
  title?: string;
  show_department_detail?: boolean;
  show_project_detail?: boolean;
  show_expenses_account_type_detail?: boolean;
  traveler_name_changes?: TravelerNameChangeResponseArgs[];
  shinkansen_deadline?: string;
  approval_route_version?: number;
  wifi_provider_options?: { [key: string]: string };
  receipt_revision_status?: string;
  can_issue_receipt?: boolean;
  receipt_total_price?: number;
  departure?: string;
};

export type TripArgs = {
  id: number;
  user: User;
  organization?: Organization;
  userId?: number;
  organizationId?: number;
  showFee: boolean;
  serviceId?: number;
  searchQueryId?: number;
  searchQuery?: SearchQuery;
  startTime?: Moment;
  endTime?: Moment;
  cacheStartTime?: Moment;
  cacheEndTime?: Moment;
  peoplenum?: number;
  finalDestination?: string;
  purpose?: string;
  internalNumber?: string;
  bakurakuWorkflowRequestId?: string;
  bakurakuWorkflowRequestTitle?: string;
  bakurakuWorkflowRequestNum?: string;
  invoiceTripType?: string;
  flightApproveExpiredAt: Moment | null;
  currentOrder?: Order;
  order?: Order;
  firstOrder?: Order;
  appliedOrder?: Order;
  approvedOrder?: Order;
  travelerInformations: TravelerInformation[];
  currentTravelerInformations: TravelerInformation[];
  ruleErrors: TripRuleError[];
  inAdvanceApprovals: InAdvanceApproval[];
  currentApprovalStage?: number;
  arrangementRequest: ArrangementRequest | null;
  nonOrderItems: NonOrderItem[];
  receivedAt?: Moment;
  createdAt: Moment;
  status?: number;
  statusStr?: string;
  approveStageCount?: number;
  progress?: string;
  departments?: Department[];
  projects?: Project[];
  orders?: Order[];
  applicant?: User;
  billing?: boolean; // true: 請求書払い, false/undefined: カード払い
  forbiddenRepositories?: ForbiddenRepositoryArgs[]; // 右ナビのSMAS利用の表示に使う
  itineraryId?: string;
  tripApproveItem?: TripApproveItem;
  lastActionAt?: Moment;
  updatedAt?: Moment;
  existsUnopendUserMessage?: boolean;
  arrangerMemo?: ArrangerMemo;
  foreignAir?: boolean;
  ticketingRequestedAt?: Moment;
  freeeApprovalRequestId?: number | null;
  invoiceId?: number;
  departureLocation?: string; // TripからではなくTransportElementから組み立てている
  destination?: string; // TripからではなくTransportElementから組み立てている
  downloadedCount?: number;
  downloadedAt?: Moment;
  draft?: boolean;
  hasUnreadReplyRequiredMessages?: boolean;
  exicUri?: string;
  businessEkinetUri?: string;
  ticketingRequestable?: boolean;
  seatReservationEnable?: boolean;
  cancelRequestable?: boolean;
  bulkCancelable?: boolean;
  bulkCancelErrorMessage?: string | null;
  bulkCancelDeadlineText?: string | null;
  premiumSupportActive?: boolean;
  reportAvailable?: boolean;
  tripReport?: TripReport;
  title?: string;
  showDepartmentDetail?: boolean;
  showProjectDetail?: boolean;
  showExpensesAccountTypeDetail?: boolean;
  travelerNameChanges?: TravelerNameChange[];
  shinkansenDeadline?: string;
  approvalRouteVersion?: number;
  receiptRevisionStatus?: 'none' | 'revising' | 'revised' | '';
  canIssueReceipt?: boolean;
  receiptTotalPrice?: number;
  departure?: string;
};

export const convertTripResponseToArgs = (response: TripResponseArgs, showFee: boolean) => {
  const currentOrderResponse = response.current_order
    ? { ...response.current_order, wifi_provider_options: response.wifi_provider_options }
    : undefined;
  const firstOrderResponse = response.first_order
    ? { ...response.first_order, wifi_provider_options: response.wifi_provider_options }
    : undefined;
  const appliedOrderResponse = response.applied_order
    ? { ...response.applied_order, wifi_provider_options: response.wifi_provider_options }
    : undefined;
  const approvedOrderResponse = response.approved_order
    ? { ...response.approved_order, wifi_provider_options: response.wifi_provider_options }
    : undefined;
  const currentOrder = currentOrderResponse
    ? new Order(convertOrderResponseToArgs(currentOrderResponse, showFee))
    : undefined;

  const args: TripArgs = {
    id: response.id,
    user: new User(convertUserResponseToArgs(response.user)),
    organization: response.organization
      ? new Organization(convertOrganizationResponseToArgs(response.organization))
      : undefined,
    userId: response.user_id,
    organizationId: response.organization_id,
    showFee,
    serviceId: response.service_id,
    searchQueryId: response.search_query_id,
    searchQuery: response.search_query
      ? new SearchQuery(convertSearchQueryResponseToArgs(response.search_query))
      : undefined,
    startTime: response.start_time ? moment(response.start_time) : undefined,
    endTime: response.end_time ? moment(response.end_time) : undefined,
    cacheStartTime: response.cache_start_time ? moment(response.cache_start_time) : undefined,
    cacheEndTime: response.cache_end_time ? moment(response.cache_end_time) : undefined,
    peoplenum: response.peoplenum,
    finalDestination: response.final_destination,
    purpose: response.purpose,
    internalNumber: response.internal_number,
    bakurakuWorkflowRequestId: response.bakuraku_workflow_request_id || undefined,
    bakurakuWorkflowRequestTitle: response.bakuraku_workflow_request_title || undefined,
    bakurakuWorkflowRequestNum: response.bakuraku_workflow_request_num || undefined,
    invoiceTripType: response.invoice_trip_type,
    flightApproveExpiredAt: response.flight_approve_expired_at ? moment(response.flight_approve_expired_at) : null,
    currentOrder,
    order: currentOrder,
    firstOrder: firstOrderResponse
      ? new Order(convertOrderResponseToArgs(firstOrderResponse, showFee))
      : undefined,
    appliedOrder: appliedOrderResponse
      ? new Order(convertOrderResponseToArgs(appliedOrderResponse, showFee))
      : undefined,
    approvedOrder: approvedOrderResponse
      ? new Order(convertOrderResponseToArgs(approvedOrderResponse, showFee))
      : undefined,
    travelerInformations:
      response.traveler_informations?.map(
        travelerInformation =>
          new TravelerInformation(convertTravelerInformationResponseToArgs(travelerInformation))
      ) || [],
    currentTravelerInformations:
      response.current_traveler_informations?.map(
        (travelerInformation: TravelerInformationResponseArgs) =>
          new TravelerInformation(convertTravelerInformationResponseToArgs(travelerInformation))
      ) || [],
    ruleErrors: response.cache_rule_errors?.map(cacheRuleError => new TripRuleError(cacheRuleError)) || [],
    inAdvanceApprovals:
      response.in_advance_approval?.map(
        inAdvanceApproval => new InAdvanceApproval(convertInAdvanceApprovalResponseToArgs(inAdvanceApproval))
      ) || [],
    currentApprovalStage: response.current_approval_stage,
    arrangementRequest: response.arrangement_request
      ? new ArrangementRequest(convertArrangementRequestResponseToArgs(response.arrangement_request))
      : null,
    nonOrderItems:
      response.non_order_items?.map(
        nonOrderItem => new NonOrderItem(convertNonOrderItemResponseToArgs(nonOrderItem))
      ) || [],
    receivedAt: response.received_at ? moment(response.received_at) : undefined,
    createdAt: moment(response.created_at),
    status: response.status,
    approveStageCount: response.approve_stage_count,
    progress: response.progress,
    departments: response.charging_departments
      ? response.charging_departments.map(
          department => new Department(convertDepartmentResponseToArgs(department))
        )
      : [],
    projects: response.projects
      ? response.projects.map(project => new Project(convertProjectResponseToArgs(project)))
      : [],
    orders:
      response.orders?.map((o: OrderResponseArgs) => new Order(convertOrderResponseToArgs(o, showFee))) || [],
    applicant: response.applicant ? new User(convertUserResponseToArgs(response.applicant)) : undefined,
    billing: response.billing,
    forbiddenRepositories:
      response.organization?.forbidden_repositories || response.user?.organization?.forbidden_repositories,
    itineraryId: response.itinerary_id,
    tripApproveItem: response.trip_approve_item
      ? new TripApproveItem(convertTripApproveItemResponseToArgs(response.trip_approve_item))
      : undefined,
    lastActionAt: response.last_action_at ? moment(response.last_action_at) : undefined,
    updatedAt: response.updated_at ? moment(response.updated_at) : undefined,
    existsUnopendUserMessage: response.exists_unopend_user_message,
    arrangerMemo: response.arranger_memo
      ? new ArrangerMemo(convertArrangerMemoResponseToArgs(response.arranger_memo))
      : undefined,
    foreignAir: response.foreign_air,
    ticketingRequestedAt: response.ticketing_requested_at ? moment(response.ticketing_requested_at) : undefined,
    freeeApprovalRequestId: response.freee_approval_request_id,
    invoiceId: response.invoice_id,
    departureLocation: response.departure_location, // 画面表示ではAPIの値はつかわないでTransportElementから組み立てる
    destination: response.destination, // 画面表示ではAPIの値はつかわないでTransportElementから組み立てる
    downloadedCount: response.downloaded_count,
    downloadedAt: response.downloaded_at ? moment(response.downloaded_at) : undefined,
    draft: response.draft,
    hasUnreadReplyRequiredMessages: response.has_unread_reply_required_messages,
    exicUri: response.exic_uri,
    businessEkinetUri: response.business_ekinet_uri || '',
    ticketingRequestable: response.ticketing_requestable,
    seatReservationEnable: response.seat_reservation_enable,
    cancelRequestable: response.cancel_requestable,
    bulkCancelable: response.bulk_cancelable,
    bulkCancelErrorMessage: response.bulk_cancel_error_message ?? null,
    bulkCancelDeadlineText: response.bulk_cancel_deadline_text ?? null,
    premiumSupportActive: response.premium_support_active,
    reportAvailable: response.report_available,
    tripReport: response.trip_report
      ? new TripReport(convertTripReportResponseToArgs(response.trip_report))
      : undefined,
    title: response.title,
    showDepartmentDetail: response.show_department_detail,
    showProjectDetail: response.show_project_detail,
    showExpensesAccountTypeDetail: response.show_expenses_account_type_detail,
    travelerNameChanges: response.traveler_name_changes?.map(
      travelerNameChange => new TravelerNameChange(convertTravelerNameChangeResponseToArgs(travelerNameChange))
    ),
    shinkansenDeadline: response.shinkansen_deadline,
    approvalRouteVersion: response.approval_route_version,
    receiptRevisionStatus: (response.receipt_revision_status as 'none' | 'revising' | 'revised' | '') || 'none',
    canIssueReceipt: response.can_issue_receipt,
    receiptTotalPrice: response.receipt_total_price,
    departure: response.departure
  };
  return args;
};

export default class Trip {
  @observable id: number;
  @observable user: User;
  @observable organization?: Organization;
  @observable userId?: number;
  @observable organizationId?: number;
  @observable showFee: boolean;
  @observable serviceId?: number;
  @observable searchQueryId?: number;
  @observable searchQuery?: SearchQuery;
  @observable search_query?: SearchQuery; // snake_caseでのアクセス用
  @observable startTime?: Moment;
  @observable endTime?: Moment;
  @observable cacheStartTime?: Moment;
  @observable cacheEndTime?: Moment;
  @observable peoplenum?: number;
  @observable finalDestination?: string;
  @observable purpose?: string;
  @observable internalNumber?: string;
  @observable bakurakuWorkflowRequestId?: string;
  @observable bakurakuWorkflowRequestTitle?: string;
  @observable bakurakuWorkflowRequestNum?: string;
  @observable invoiceTripType?: string;
  @observable flightApproveExpiredAt: Moment | null;
  @observable currentOrder?: Order;
  @observable order?: Order;
  @observable firstOrder?: Order;
  @observable appliedOrder?: Order;
  @observable approvedOrder?: Order;
  @observable travelerInformations: TravelerInformation[];
  @observable currentTravelerInformations: TravelerInformation[];
  @observable ruleErrors: TripRuleError[];
  @observable inAdvanceApprovals: InAdvanceApproval[];
  @observable currentApprovalStage?: number;
  @observable arrangementRequest: ArrangementRequest | null;
  @observable nonOrderItems: NonOrderItem[];
  @observable receivedAt?: Moment;
  @observable createdAt: Moment;
  @observable status?: number;
  @observable statusStr?: string;
  @observable approveStageCount?: number;
  @observable progress?: string;
  @observable departments?: Department[];
  @observable projects?: Project[];
  @observable orders?: Order[];
  @observable applicant?: User;
  @observable billing?: boolean; // true: 請求書払い, false/undefined: カード払い
  @observable forbiddenRepositories?: ForbiddenRepositoryArgs[];
  @observable itineraryId?: string;
  @observable tripApproveItem?: TripApproveItem;
  @observable lastActionAt?: Moment;
  @observable updatedAt?: Moment;
  @observable existsUnopendUserMessage?: boolean;
  @observable arrangerMemo?: ArrangerMemo;
  @observable foreignAir: boolean;
  @observable ticketingRequestedAt?: Moment;
  @observable freeeApprovalRequestId?: number | null;
  @observable invoiceId?: number;
  @observable departureLocation?: string; // 値は空になる前提で、出発地の組み立ててフォールバックする
  @observable destination?: string; // 値は空になる前提で、目的地の組み立ててフォールバックする
  @observable downloadedCount?: number;
  @observable downloadedAt?: Moment;
  @observable draft?: boolean;
  @observable hasUnreadReplyRequiredMessages?: boolean;
  @observable exicUri?: string;
  @observable businessEkinetUri?: string;
  @observable ticketingRequestable?: boolean;
  @observable seatReservationEnable?: boolean;
  @observable cancelRequestable?: boolean;
  @observable bulkCancelable?: boolean;
  @observable bulkCancelErrorMessage?: string | null;
  @observable bulkCancelDeadlineText?: string | null;
  @observable premiumSupportActive?: boolean;
  @observable reportAvailable?: boolean;
  @observable tripReport?: TripReport;
  @observable title?: string;
  @observable showDepartmentDetail?: boolean;
  @observable showProjectDetail?: boolean;
  @observable showExpensesAccountTypeDetail?: boolean;
  @observable travelerNameChanges?: TravelerNameChange[];
  @observable shinkansenDeadline?: string;
  @observable approvalRouteVersion?: number;
  @observable receiptRevisionStatus?: 'none' | 'revising' | 'revised' | '';
  @observable canIssueReceipt?: boolean;
  @observable receiptTotalPrice?: number;
  @observable departure?: string;
  private readonly textLength: number = 10;

  constructor(args: TripArgs) {
    this.id = args.id;
    this.user = args.user;
    this.organization = args.organization;
    this.userId = args.userId;
    this.organizationId = args.organizationId;
    this.showFee = args.showFee;
    this.serviceId = args.serviceId;
    this.searchQueryId = args.searchQueryId;
    this.searchQuery = args.searchQuery;
    this.search_query = args.searchQuery; // snake_caseでもアクセスできるように
    this.startTime = args.startTime;
    this.endTime = args.endTime;
    this.cacheStartTime = args.cacheStartTime;
    this.cacheEndTime = args.cacheEndTime;
    this.peoplenum = args.peoplenum;
    this.finalDestination = args.finalDestination;
    this.purpose = args.purpose;
    this.internalNumber = args.internalNumber;
    this.bakurakuWorkflowRequestId = args.bakurakuWorkflowRequestId;
    this.bakurakuWorkflowRequestTitle = args.bakurakuWorkflowRequestTitle;
    this.bakurakuWorkflowRequestNum = args.bakurakuWorkflowRequestNum;
    this.invoiceTripType = args.invoiceTripType;
    this.flightApproveExpiredAt = args.flightApproveExpiredAt;
    this.currentOrder = args.currentOrder;
    this.order = args.order;
    this.firstOrder = args.firstOrder;
    this.appliedOrder = args.appliedOrder;
    this.approvedOrder = args.approvedOrder;
    this.travelerInformations = args.travelerInformations;
    this.currentTravelerInformations = args.currentTravelerInformations;
    this.ruleErrors = args.ruleErrors;
    this.inAdvanceApprovals = args.inAdvanceApprovals;
    this.currentApprovalStage = args.currentApprovalStage;
    this.arrangementRequest = args.arrangementRequest;
    this.nonOrderItems = args.nonOrderItems;
    this.receivedAt = args.receivedAt;
    this.createdAt = args.createdAt;
    this.status = args.status;
    this.statusStr = args.statusStr;
    this.approveStageCount = args.approveStageCount;
    this.progress = args.progress;
    this.departments = args.departments;
    this.projects = args.projects;
    this.orders = args.orders;
    this.applicant = args.applicant;
    this.billing = args.billing;
    this.forbiddenRepositories = args.forbiddenRepositories;
    this.itineraryId = args.itineraryId;
    this.tripApproveItem = args.tripApproveItem;
    this.lastActionAt = args.lastActionAt;
    this.updatedAt = args.updatedAt;
    this.existsUnopendUserMessage = args.existsUnopendUserMessage;
    this.arrangerMemo = args.arrangerMemo;
    this.foreignAir = args.foreignAir || false;
    this.ticketingRequestedAt = args.ticketingRequestedAt;
    this.freeeApprovalRequestId = args.freeeApprovalRequestId;
    this.invoiceId = args.invoiceId;
    this.departureLocation = args.departureLocation; // サーバーサイドに処理を寄せたら意味がある
    this.destination = args.destination; // サーバーサイドに処理を寄せたら意味がある
    this.downloadedCount = args.downloadedCount;
    this.downloadedAt = args.downloadedAt;
    this.draft = args.draft;
    this.hasUnreadReplyRequiredMessages = args.hasUnreadReplyRequiredMessages;
    this.exicUri = args.exicUri;
    this.businessEkinetUri = args.businessEkinetUri;
    this.ticketingRequestable = args.ticketingRequestable;
    this.seatReservationEnable = args.seatReservationEnable;
    this.cancelRequestable = args.cancelRequestable;
    this.bulkCancelable = args.bulkCancelable;
    this.bulkCancelErrorMessage = args.bulkCancelErrorMessage ?? null;
    this.bulkCancelDeadlineText = args.bulkCancelDeadlineText ?? null;
    this.premiumSupportActive = args.premiumSupportActive;
    this.reportAvailable = args.reportAvailable;
    this.tripReport = args.tripReport;
    this.title = args.title;
    this.showDepartmentDetail = args.showDepartmentDetail;
    this.showProjectDetail = args.showProjectDetail;
    this.showExpensesAccountTypeDetail = args.showExpensesAccountTypeDetail;
    this.travelerNameChanges = args.travelerNameChanges;
    this.shinkansenDeadline = args.shinkansenDeadline;
    this.approvalRouteVersion = args.approvalRouteVersion;
    this.receiptRevisionStatus = args.receiptRevisionStatus || '';
    this.canIssueReceipt = args.canIssueReceipt;
    this.receiptTotalPrice = args.receiptTotalPrice;
    this.departure = args.departure;
  }

  get dateRange() {
    const start = this.cacheStartTime || this.startTime;
    const end = this.cacheEndTime || this.endTime;
    return `${start?.format('YYYY/MM/DD')}〜${end?.format('YYYY/MM/DD')}`;
  }

  get currentApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage
    );
  }

  get currentApprovalStatus() {
    return this.currentApproval?.status;
  }

  get nextApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval =>
        this.currentApprovalStage != null && inAdvanceApproval.approveStage === this.currentApprovalStage + 1
    );
  }

  get totalPrice() {
    return (this.order?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get nonOrderItemsTotalPrice() {
    return this.nonOrderItems.reduce((acc, nonOrderItem) => acc + nonOrderItem.totalPrice, 0);
  }

  nonOrderItemsTotalPriceByTypes(types: string[]) {
    const items =
      types.length === 0 ? this.nonOrderItems : this.nonOrderItems.filter(item => item.isElementTypes(types));
    if (items.length === 0) return 0;
    return items.reduce((acc, nonOrderItem) => acc + nonOrderItem.totalPrice, 0);
  }

  get oldTotalPrice() {
    return (this.firstOrder?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get didTotalPriceChange() {
    return this.totalPrice !== this.oldTotalPrice;
  }

  get isDepartmentMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isDepartmentNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isProjectMappingsExist() {
    return this.order?.orderItems.some(item => item.orderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isProjectNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item => item.nonOrderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isExpensesAccountTypeMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isExpensesAccountTypeNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isHotelCheckinExpired() {
    return this.order?.orderItems.some(item =>
      item.hotels.some(
        hotel => hotel.tooLate || (hotel.checkinDate && hotel.checkinDate.isBefore(moment(), 'day'))
      )
    );
  }

  get flightTooLateError() {
    return this.flightApproveExpiredAt && moment().isSameOrAfter(this.flightApproveExpiredAt);
  }

  get isApproveCanceled() {
    return this.inAdvanceApprovals.some(approver => approver.status === 3);
  }

  get tripDetailEditable() {
    return this.inAdvanceApprovals.length === 0 || !this.inAdvanceApprovals[0]?.status;
  }

  get cancelReason() {
    const cancelApproval = this.inAdvanceApprovals.find(approver => approver.status === 3);
    return cancelApproval?.cancelReason;
  }

  isBackStageApprover(userId: number) {
    if (this.currentApprovalStage == null) {
      return false;
    }
    const candidateStages = this.inAdvanceApprovals
      .filter(approval =>
        approval.inAdvanceApproverCandidates.some(candidate => candidate.approverCandidateId === userId)
      )
      .map(approval => approval.approveStage);
    if (candidateStages.length === 0) {
      return false;
    }
    const minApproveStage = Math.min(...candidateStages);
    return this.currentApprovalStage < minApproveStage;
  }

  isCurrentStageApprover(userId: number) {
    return !!this.currentApproval?.inAdvanceApproverCandidates.some(
      candidate => candidate.approverCandidateId === userId
    );
  }

  approveReason(num: number) {
    return this.inAdvanceApprovals[num].approveReason;
  }

  rejectReason(num: number) {
    return this.inAdvanceApprovals[num].rejectReason;
  }

  get isConfirmationRequestable() {
    // 確認依頼が可能かどうかを判定
    return this.currentOrder?.travelerConfirmationStatus === 'unrequested';
  }

  get isFixed() {
    // 確定済みかどうか
    return this.status === 4;
  }

  get isAllDone() {
    // すべて完了しているかどうか
    if (this.status !== 7) return false;

    let result = false;
    this.order?.orderItems.forEach(item => {
      if (item.status === 7) {
        result = true;
      }
    });
    return result;
  }

  // 予約が必要な状態かどうか
  get isReserveRequired(): boolean {
    return this.status === 3;
  }

  // 国内航空券の予約番号または確認番号が未入力かどうか
  get isAirNumbersMissing(): boolean {
    let result = false;
    this.order?.orderItems?.forEach(item => {
      item.elements?.forEach(el => {
        // TransportElementの場合のみチェック
        if (this.isTransportElement(el)) {
          if (el.isDomesticAir && el.isAirNumbersMissing) {
            result = true;
          }
        }
      });
    });
    return result;
  }

  get isTicketingRequested() {
    return this.foreignAir && this.ticketingRequestedAt;
  }

  get cancelFeeText() {
    if (this.isTicketingRequested) {
      return this.currentOrder?.isTicketingExpired ? '取消料 + 5,000円' : '5,000円';
    }
    return '0円';
  }

  get originText() {
    return this.departureLocation || this.buildDepartureLocation || '';
  }

  get destinationText() {
    return this.destination || this.buildDestination || '';
  }

  get isRejected(): boolean {
    return this.inAdvanceApprovals.some(approver => approver.status === 2);
  }

  get isAllOrderItemStatusComplete(): boolean {
    return this.order?.orderItems.every(item => item.status === 7) || false;
  }

  get isEndTimeInFuture() {
    const time = this.cacheEndTime || this.endTime;
    return !!time && time.isAfter(moment());
  }

  get departure_time(): Moment | undefined {
    return this.startTime;
  }

  get return_time(): Moment | undefined {
    return this.endTime;
  }

  get travelerNames() {
    return this.travelerInformations.map(traveler => traveler.travelerName);
  }

  get isApproved() {
    return this.inAdvanceApprovals.every(approver => approver.status === 1);
  }

  get originTitle() {
    const departureLocation = this.departureLocation || this.buildDepartureLocation;
    return departureLocation && departureLocation.length > this.textLength ? departureLocation : undefined;
  }

  get destinationTitle() {
    const destination = this.destination || this.buildDestination;
    if (destination && destination.length > this.textLength) {
      return destination;
    }
    return undefined;
  }

  // サーバーサイドに処理を移植して素直にAPIレスポンスを表示したい
  get buildDepartureLocation() {
    const hasOrderItems = this.order?.orderItems && this.order.orderItems.length > 0;
    const hasNonOrderItems = this.nonOrderItems && this.nonOrderItems.length > 0;

    // orderItemsの処理
    if (hasOrderItems && this.order?.orderItems) {
      const elements = this.order.orderItems.flatMap(item => item.transports.filter(e => e.from !== undefined));
      return elements.length > 0 ? elements[0].from.name : '';
    }

    // nonOrderItemsの処理
    if (hasNonOrderItems) {
      const elements = this.nonOrderItems.flatMap(item => item.transports.filter(e => e.from !== undefined));
      return elements[0]?.from?.name || elements[0]?.boardingStation || '';
    }

    return '';
  }

  // サーバーサイドに処理を移植して素直にAPIレスポンスを表示したい
  get buildDestination() {
    const from = this.buildDepartureLocation;

    // orderItemsから目的地の情報を取得
    if (this.order?.orderItems?.length) {
      const elements = this.order.orderItems.flatMap(item =>
        item.transports.filter(e => e.to !== undefined && e.to.name !== from)
      );
      return elements.length > 0 ? elements.map(e => e.to.name).join('、') : '';
    }

    // nonOrderItemsから目的地の情報を取得
    if (this.nonOrderItems.length > 0) {
      const elements = this.nonOrderItems.flatMap(item =>
        item.transports.filter(e => e.to !== undefined && e.to.name !== from)
      );
      return elements.length > 0 ? elements.map(e => e.to?.name || e.arrivalStation).join('、') : '';
    }

    return '';
  }

  get totalPriceWithBuffer() {
    return (this.order?.totalPriceWithBuffer || 0) + this.nonOrderItemsTotalPrice;
  }

  get totalBufferAmount() {
    return this.order?.totalBufferAmount || 0;
  }

  get nextStageApprovers() {
    const nextApprovals = this.inAdvanceApprovals.find(
      inAdvanceApproval =>
        this.currentApprovalStage != null && inAdvanceApproval.approveStage === this.currentApprovalStage + 1
    );
    return (
      nextApprovals?.inAdvanceApproverCandidates.map(
        inAdvanceApproverCandidate => inAdvanceApproverCandidate.approverCandidate.name
      ) ?? []
    );
  }

  // 型ガード: TransportElementかどうかを判定
  private isTransportElement(element: OrderItemElement): element is TransportElement {
    return 'type' in element && element.type === 'transport';
  }

  // 指定されたorderItemの初期状態を返す
  initialOrderItemFor(orderItem: OrderItem): OrderItem | undefined {
    const initialOrder = this.approvedOrder || this.firstOrder;
    if (!initialOrder) return undefined;
    return initialOrder.orderItems.find(item => item.traceId === orderItem.traceId);
  }
}
